import React, { useState } from "react";
import { ChevronDown, ChevronUp, Target, Award } from "lucide-react";

const METRIC_NAMES = {
  sj: "Squat Jump", vj: "Vertical Jump", bj: "Broad Jump", rsi: "RSI", ag: "Approach Gain",
};

const METRIC_UNITS = {
  sj: "in", vj: "in", bj: "in", rsi: "", ag: "in",
};

const getDomColor = (val) => {
  if (val >= 100) return "text-emerald-300";
  if (val >= 75) return "text-amber-300";
  return "text-red-400";
};

const getDomBg = (val) => {
  if (val >= 100) return "bg-emerald-500/15 border-emerald-400/40";
  if (val >= 75) return "bg-amber-500/15 border-amber-400/40";
  return "bg-red-500/15 border-red-400/40";
};

const getScoreColor = (pctAvg) => {
  if (pctAvg >= 100) return { text: "text-emerald-300", bg: "bg-emerald-500/20", border: "border-emerald-400/40", label: "Above Avg" };
  if (pctAvg >= 85) return { text: "text-amber-300", bg: "bg-amber-500/20", border: "border-amber-400/40", label: "Near Avg" };
  return { text: "text-red-400", bg: "bg-red-500/20", border: "border-red-400/40", label: "Below Avg" };
};

const getRatioColor = (label) => {
  if (label === "Elite") return { text: "text-emerald-300", bg: "bg-emerald-500/20", border: "border-emerald-400/40" };
  if (label === "Very Good") return { text: "text-blue-300", bg: "bg-blue-500/20", border: "border-blue-400/40" };
  if (label === "Good") return { text: "text-amber-300", bg: "bg-amber-500/20", border: "border-amber-400/40" };
  return { text: "text-red-400", bg: "bg-red-500/20", border: "border-red-400/40" };
};

function ExpandableSection({ title, icon: Icon, defaultOpen = false, badge, badgeColor, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl overflow-hidden border border-[#2a3554]" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 md:p-5 text-left bg-[#141B2D] hover:bg-[#1a2236] transition-colors"
        data-testid={`${testId}-toggle`}
      >
        {Icon && <Icon className="w-5 h-5 md:w-6 md:h-6 text-slate-300 flex-shrink-0" />}
        <span className="font-bold text-white text-base md:text-lg flex-1">{title}</span>
        {badge && (
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${badgeColor || "bg-blue-500/20 text-blue-300"}`}>
            {badge}
          </span>
        )}
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 md:p-5 bg-[#0f172a]/60 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

function TestCard({ name, metricKey, data, isLimiter, report }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const scoreStyle = getScoreColor(data.pct_avg || 0);

  return (
    <div
      className={`rounded-xl overflow-hidden border-2 transition-colors ${
        isLimiter ? "border-red-500/50 bg-red-500/5" : "border-[#2a3554] bg-[#141B2D]"
      }`}
      data-testid={`test-card-${metricKey}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 md:p-5 text-left hover:bg-[#1a2236]/50 transition-colors"
        data-testid={`test-card-${metricKey}-toggle`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-white text-base md:text-lg">{name}</span>
            {isLimiter && (
              <span className="text-xs font-bold text-red-300 bg-red-500/25 px-2.5 py-0.5 rounded-full border border-red-500/40">
                LIMITER
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-2xl md:text-3xl font-bold text-white">
              {data.value}{METRIC_UNITS[metricKey] ? <span className="text-lg md:text-xl text-slate-300 ml-1">{METRIC_UNITS[metricKey]}</span> : ""}
            </span>
            <span className={`text-sm font-semibold px-3 py-1 rounded-full ${scoreStyle.bg} ${scoreStyle.text} ${scoreStyle.border} border`}>
              {data.pct_avg}% of avg
            </span>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 md:px-5 pb-4 md:pb-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0f172a] rounded-xl p-4 text-center border border-[#1E293B]">
              <p className="text-sm text-slate-300 font-medium mb-1">vs Age Group Avg</p>
              <p className="text-3xl md:text-4xl font-bold text-emerald-300">{data.pct_avg}%</p>
              <p className="text-sm text-slate-300 mt-2">Avg: <span className="font-semibold text-white">{data.avg}{METRIC_UNITS[metricKey] ? ` ${METRIC_UNITS[metricKey]}` : ""}</span></p>
            </div>
            <div className="bg-[#0f172a] rounded-xl p-4 text-center border border-[#1E293B]">
              <p className="text-sm text-slate-300 font-medium mb-1">vs Elite Level</p>
              <p className="text-3xl md:text-4xl font-bold text-blue-300">{data.pct_elite}%</p>
              <p className="text-sm text-slate-300 mt-2">Elite: <span className="font-semibold text-white">{data.elite}{METRIC_UNITS[metricKey] ? ` ${METRIC_UNITS[metricKey]}` : ""}</span></p>
            </div>
          </div>
          {isLimiter && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <p className="text-sm md:text-base text-red-200 leading-relaxed">
                This is <strong className="text-white">{report.athlete_name}'s</strong> primary limiter. Improving this area will have the biggest impact on overall performance.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AssessmentResultsView({ report }) {
  if (!report) return null;

  const metrics = report.metrics || {};
  const dom = report.domination || {};
  const testOrder = [
    ["sj", "Squat Jump"], ["vj", "Vertical Jump"], ["bj", "Broad Jump"],
    ["rsi", "RSI"], ["ag", "Approach Gain"],
  ];
  const availableTests = testOrder.filter(([key]) => metrics[key]);

  return (
    <div className="space-y-4" data-testid="assessment-results-view">
      {/* Athlete Info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#141B2D] rounded-xl p-4 border border-[#2a3554]">
          <p className="text-sm text-slate-300 mb-1">Parent</p>
          <p className="text-base md:text-lg text-white font-semibold">{report.parent_name}</p>
        </div>
        <div className="bg-[#141B2D] rounded-xl p-4 border border-[#2a3554]">
          <p className="text-sm text-slate-300 mb-1">Sport</p>
          <p className="text-base md:text-lg text-white font-semibold capitalize">{report.sport}</p>
        </div>
      </div>

      {/* OVERALL SUMMARY */}
      <ExpandableSection
        title="Overall Performance"
        icon={Award}
        defaultOpen={true}
        badge={`Stage ${report.stage_score}%`}
        badgeColor="bg-emerald-500/20 text-emerald-300"
        testId="overall-section"
      >
        {/* Stage + Elite */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#141B2D] rounded-xl p-5 md:p-6 text-center border border-emerald-500/20">
            <p className="text-sm md:text-base text-slate-200 uppercase tracking-wide font-medium">Stage Score</p>
            <p className="text-4xl md:text-5xl font-bold text-emerald-300 mt-2">{report.stage_score}%</p>
            <p className="text-sm text-slate-300 mt-2">vs Age Group</p>
          </div>
          <div className="bg-[#141B2D] rounded-xl p-5 md:p-6 text-center border border-blue-500/20">
            <p className="text-sm md:text-base text-slate-200 uppercase tracking-wide font-medium">Elite Score</p>
            <p className="text-4xl md:text-5xl font-bold text-blue-300 mt-2">{report.elite_score}%</p>
            <p className="text-sm text-slate-300 mt-2">vs Elite Level</p>
          </div>
        </div>

        {/* Domination levels */}
        <div>
          <p className="text-sm md:text-base text-slate-200 uppercase tracking-wide mb-3 font-medium">Domination Levels</p>
          <div className="grid grid-cols-4 gap-2 md:gap-3">
            {[["middle_school", "MS"], ["high_school", "HS"], ["college", "COL"], ["pro", "PRO"]].map(([k, l]) => {
              const val = dom[k] || 0;
              return (
                <div key={k} className={`rounded-xl p-3 md:p-4 text-center border-2 ${getDomBg(val)}`}>
                  <p className="text-sm text-slate-200 font-medium">{l}</p>
                  <p className={`text-xl md:text-2xl font-bold mt-1 ${getDomColor(val)}`}>{val}%</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Strengths & Limiter */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {report.strengths && report.strengths.length > 0 && (
            <div className="bg-emerald-500/10 border-2 border-emerald-400/30 rounded-xl p-4">
              <p className="text-sm font-bold text-emerald-300 uppercase mb-2">Strengths</p>
              <p className="text-base text-emerald-100 font-medium">{report.strengths.map(s => METRIC_NAMES[s] || s).join(", ")}</p>
            </div>
          )}
          {report.primary_limiter && (
            <div className="bg-red-500/10 border-2 border-red-400/30 rounded-xl p-4">
              <p className="text-sm font-bold text-red-300 uppercase mb-2">Primary Limiter</p>
              <p className="text-base text-red-100 font-medium">{METRIC_NAMES[report.primary_limiter] || report.primary_limiter}</p>
            </div>
          )}
        </div>
      </ExpandableSection>

      {/* INDIVIDUAL TEST CARDS */}
      <div>
        <p className="text-sm text-slate-300 uppercase tracking-wide mb-3 px-1 font-medium">Tap a test to see details</p>
        <div className="space-y-3">
          {availableTests.map(([key, name]) => (
            <TestCard
              key={key}
              name={name}
              metricKey={key}
              data={metrics[key]}
              isLimiter={key === report.primary_limiter}
              report={report}
            />
          ))}
        </div>
      </div>

      {/* RSI / VJ RATIO */}
      {report.rsi_vj_ratio && (
        <div className="rounded-xl overflow-hidden border-2 border-[#2a3554] bg-[#141B2D]" data-testid="rsi-vj-ratio-card">
          <div className="p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-white text-base md:text-lg">RSI Jump Height / Vertical Jump</span>
              {(() => {
                const style = getRatioColor(report.rsi_vj_label);
                return (
                  <span className={`text-sm font-bold px-3 py-1 rounded-full border ${style.bg} ${style.text} ${style.border}`}>
                    {report.rsi_vj_label}
                  </span>
                );
              })()}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl md:text-4xl font-bold text-white">{report.rsi_vj_ratio}%</span>
              <span className="text-sm text-slate-300">
                ({report.rsi_jump_height} in / {report.metrics?.vj?.value || "—"} in)
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4">
              {[
                { label: "Average", range: "60-70%", active: report.rsi_vj_label === "Average" },
                { label: "Good", range: "70-80%", active: report.rsi_vj_label === "Good" },
                { label: "Very Good", range: "80-90%", active: report.rsi_vj_label === "Very Good" },
                { label: "Elite", range: "90%+", active: report.rsi_vj_label === "Elite" },
              ].map(({ label, range, active }) => (
                <div key={label} className={`rounded-lg p-2 md:p-3 text-center border ${active ? "border-white/40 bg-white/10" : "border-[#1E293B] bg-[#0f172a]"}`}>
                  <p className={`text-xs font-bold ${active ? "text-white" : "text-slate-400"}`}>{label}</p>
                  <p className={`text-sm mt-0.5 ${active ? "text-white font-semibold" : "text-slate-500"}`}>{range}</p>
                </div>
              ))}
            </div>
            {report.rsi_gct && (
              <p className="text-sm text-slate-300 mt-3">Ground Contact Time: <span className="font-semibold text-white">{report.rsi_gct}s</span></p>
            )}
          </div>
        </div>
      )}

      {/* ANALYSIS */}
      {report.analysis && (
        <ExpandableSection
          title="Coach Analysis"
          icon={Target}
          defaultOpen={false}
          testId="analysis-section"
        >
          <p className="text-base text-slate-200 leading-relaxed whitespace-pre-line">{report.analysis}</p>
        </ExpandableSection>
      )}
    </div>
  );
}
